<script lang="ts" setup>
import { AppTypography } from '~/components/base';
import type { Lobbyist } from '~/types/Candidates';

defineProps<{
  lobbyist: Lobbyist;
}>();
</script>

<template>
  <component
    :is="lobbyist.url ? 'a' : 'div'"
    :href="lobbyist.url || undefined"
    :target="lobbyist.url ? '_blank' : undefined"
    :rel="lobbyist.url ? 'noopener noreferrer' : undefined"
    data-testid="lobbyist-row"
    class="lobbyist-row flex items-center gap-4 no-underline"
  >
    <div class="lobbyist-row__text min-w-0 flex-1">
      <AppTypography type="body-bold" element="p" class="truncate">
        {{ lobbyist.name }}
      </AppTypography>
      <AppTypography
        type="subheadline"
        element="p"
        class="text-color-tag truncate"
      >
        {{ lobbyist.industry }}
      </AppTypography>
      <AppTypography
        v-if="lobbyist.description"
        type="subheadline"
        element="p"
        data-testid="lobbyist-description"
        class="lobbyist-row__description"
      >
        {{ lobbyist.description }}
      </AppTypography>
    </div>

    <Icon
      v-if="lobbyist.url"
      name="solar:square-top-down-linear"
      size="24"
      class="lobbyist-row__external shrink-0"
    />
  </component>
</template>

<style scoped lang="scss">
.lobbyist-row {
  padding: $spacing-lg;
  border: 1px solid $surface-color-disabled;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;
  color: inherit;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgb(0 0 0 / 6%);
  }

  &__icon {
    width: 44px;
    height: 44px;
    // Figma: complementary/default (#d1b37a)
    border: 2px solid #d1b37a;
    border-radius: 9999px;
    // Figma: text/brand-secondary (#3f554a)
    color: #3f554a;
    font-size: 1.25rem;
  }

  &__logo {
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: cover;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
  }

  &__description {
    // Column gap (4px) plus this = 8px, so the blurb sits apart from the name/industry pair.
    margin-top: $spacing-xs;
    // Admin text may carry intentional line breaks; keep them, wrap the rest.
    white-space: pre-line;
    overflow-wrap: anywhere;
  }
}
</style>
